import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLine, Overline, SectionHeading } from "../components/Reveal";
import { AbstractVisual, ParticleField } from "../components/AbstractVisual";
import { PartnersSection } from "../components/PartnersSection";
import { api } from "../lib/api";

const VALUES = ["Innovation", "Storytelling", "Experience", "Partnership", "Global Thinking", "Execution"];

export default function About() {
  const [jobs, setJobs] = useState([]);
  useEffect(() => { api.list("jobs").then(setJobs).catch(() => {}); }, []);

  return (
    <main>
      <Seo title="About" path="/about"
        description="Vrova is building the future of immersive entertainment — creating, distributing and operating world-class VR/XR experiences from India for the world." />
      <section className="relative min-h-[75vh] flex items-end overflow-hidden">
        <AbstractVisual hue={260} seed={61} />
        <ParticleField hue={260} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030305] via-[#030305]/40 to-[#030305]/70" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 pb-24 w-full">
          <Overline className="mb-6">About Vrova</Overline>
          <h1 className="font-serif tracking-tight leading-[1.0] text-5xl md:text-7xl lg:text-8xl" data-testid="about-headline">
            <MaskedLine delay={0.25}>Building the Future of</MaskedLine>
            <MaskedLine delay={0.4}><span className="italic text-zinc-400">Immersive Entertainment.</span></MaskedLine>
          </h1>
        </div>
      </section>

      <section className="py-28 md:py-36 border-t border-white/[0.06]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal><Overline className="mb-5">Our Story</Overline>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight">Entertainment Is Moving <span className="italic text-zinc-400">Beyond Screens.</span></h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.15}>
              <p className="text-zinc-300 font-light leading-relaxed mb-6">
                For a century, audiences watched stories. Now they want to step inside them. Vrova was founded on a simple belief: the next era of entertainment will be spatial, social and unforgettable — and it should be accessible to audiences everywhere, not just in a handful of global capitals.
              </p>
              <p className="text-zinc-400 font-light leading-relaxed">
                We operate as an XR production studio, a global immersive-content distributor, an experiential entertainment company and a location-based VR operator — four capabilities, one mission.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <PartnersSection />

      <section className="py-24 md:py-32 border-t border-white/[0.06]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            ["Mission", "Make extraordinary immersive experiences accessible to audiences everywhere."],
            ["Vision", "Build one of the world's leading immersive entertainment and XR distribution networks."],
          ].map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.1}>
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-10 md:p-14 h-full">
                <Overline className="mb-5">{t}</Overline>
                <p className="font-serif text-2xl md:text-3xl leading-snug tracking-tight">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 mt-20">
          <Reveal><Overline className="mb-8">Our Values</Overline></Reveal>
          <div className="flex flex-wrap gap-3">
            {VALUES.map((v, i) => (
              <Reveal key={v} delay={i * 0.04}>
                <span className="inline-block px-7 py-4 rounded-full border border-white/10 font-serif text-lg md:text-xl text-zinc-200">{v}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="careers" className="py-24 md:py-32 border-t border-white/[0.06]" data-testid="careers-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <SectionHeading overline="Careers" title={<>Build Worlds <span className="italic text-zinc-400">With Us.</span></>} />
          <div className="mt-14 border-t border-white/[0.07]">
            {jobs.map((j) => (
              <div key={j.id} data-testid={`job-${j.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center border-b border-white/[0.07] py-8 px-2 md:px-6 hover:bg-white/[0.02] transition-colors duration-500">
                <h3 className="md:col-span-5 font-serif text-xl md:text-2xl tracking-tight">{j.title}</h3>
                <p className="md:col-span-3 text-sm text-zinc-500 font-light">{j.detail}</p>
                <p className="md:col-span-2 text-xs font-mono text-zinc-500">{j.location}</p>
                <p className="md:col-span-2 text-xs font-mono text-zinc-500 md:text-right">{j.type}</p>
              </div>
            ))}
          </div>
          <Reveal className="mt-14">
            <Link to="/contact" data-testid="careers-cta" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-white/20 text-sm hover:bg-white/10 transition-colors">
              Introduce Yourself <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
